import parse from 'html-react-parser';
import { graphql, PageProps } from 'gatsby';
import { useTranslation } from 'gatsby-plugin-react-i18next';

import { BackNavigation } from '../components/BackNavigation';
import { Layout } from '../components/Layout';
import { Seo } from '../components/Seo';
import { Gallery, GalleryProps } from '../components/Gallery';

const SLIDER_OPTIONS: GalleryProps['options'] = {
  freeScroll: true,
  contain: true,
  wrapAround: true,
  pageDots: true,
};

const ReferencePage = ({ data }: PageProps<Queries.ReferencePageQuery>) => {
  const colorScheme = 'color-scheme4';
  const { t } = useTranslation();

  if (!data.reference) {
    return null;
  }

  return (
    <Layout data={data}>
      <Seo
        title={data.reference.title!}
        bodyAttributes={{
          class: colorScheme,
        }}
      />
      <div className='template-reference'>
        <BackNavigation title={data.reference.title!} />
        <div className='main-image'>
          {data.reference.featured_image && (
            <img
              alt={data.reference.featured_image.alternativeText!}
              src={data.reference.featured_image.localFile!.publicURL!}
            />
          )}
        </div>
        <div className='content'>
          <div>
            {data.reference.secondary_image && (
              <img
                alt={data.reference.secondary_image.alternativeText!}
                src={data.reference.secondary_image.localFile!.publicURL!}
              />
            )}
          </div>
          <div>
            <div>
              <ul>
                {data.reference.project && (
                  <li>
                    {t('PROJECT')}: {data.reference.project}
                  </li>
                )}
                {data.reference.location && (
                  <li>
                    {t('LOCATION')}: {data.reference.location}
                  </li>
                )}
                {data.reference.products?.map((product) => (
                  <li key={product!.id!}>
                    {t('PRODUCT')}: {product!.title}
                  </li>
                ))}
                {data.reference.material && (
                  <li>
                    {t('MATERIAL')}: {data.reference.material}
                  </li>
                )}
                {data.reference.finishing && (
                  <li>
                    {t('FINISHING')}: {data.reference.finishing}
                  </li>
                )}
                {data.reference.body?.data && (
                  <li>{parse(data.reference.body.data.body)}</li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className='gallery'>
          {data.reference.gallery && (
            <Gallery
              data={data.reference.gallery.map((slide) => ({
                image: slide!.localFile!.publicURL!,
              }))}
              options={SLIDER_OPTIONS}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ReferencePage;

export const query = graphql`
  query ReferencePage($language: String!, $slug: String!) {
    ...Layout

    reference: strapiReference(slug: { eq: $slug }) {
      body {
        data {
          body
        }
      }
      featured_image {
        alternativeText
        localFile {
          publicURL
        }
      }
      finishing
      gallery {
        alternativeText
        id
        localFile {
          publicURL
        }
      }
      location
      material
      products {
        id
        title
      }
      project
      secondary_image {
        alternativeText
        localFile {
          publicURL
        }
      }
      slug
      title
    }
  }
`;
